/* eslint-disable max-lines */
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Col, Form as AntForm, Row } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Button } from 'src/components/Button/Button';
import { Card } from 'src/components/Card/Card';
import { CheckboxGroupField } from 'src/components/Form/CheckboxGroupField';
import { NumberField } from 'src/components/Form/NumberField';
import { RadioGroupField } from 'src/components/Form/RadioGroupField';
import { SelectField } from 'src/components/Form/SelectField';
import { TextAreaField } from 'src/components/Form/TextAreaField';
import { TextField } from 'src/components/Form/TextField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import {
    CreatePartnerRequest,
    CreateProductRequest,
} from 'src/generated-api-client';
import { CountryResourceStore } from 'src/stores/Resources/CountryResourceStore';
import { JahezComissionTypeResourceStore } from 'src/stores/Resources/JahezComissionTypeResourceStore';
import { PartnerTypeResourceStore } from 'src/stores/Resources/PartnerTypeResourceStore';
import { ProductOfferingTypeResourceStore } from 'src/stores/Resources/ProductOfferingTypeResourceStore';
import { SpecializationResourceStore } from 'src/stores/Resources/SpecializationResourceStore';
import { JahezComissionType } from 'src/types/jahezComissionType';
import { FormHelper } from 'src/utils/FormHelper';

export const PartnerCreateForm = observer(() => {
    const form = AntForm.useFormInstance();
    const jahezComissionType: CreatePartnerFormValues['jahezComissionType'] =
        AntForm.useWatch(formNames.getPath('jahezComissionType'), form);

    const products: CreatePartnerFormValues['products'] = AntForm.useWatch(
        formNames.getPath('products'),
        form,
    );

    return (
        <>
            <Card title={t('App.Pages.PartnersCreate.GeneralInfo')}>
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath('arabicName')}
                            label={t('Partner.ArabicName')}
                            rules={[ValidationRules.required]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath('englishName')}
                            label={t('Partner.EnglishName')}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath('erpCode')}
                            label={t('Partner.ERPCode')}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <SelectField
                            name={formNames.getPath('partnerType')}
                            label={t('Partner.PartnerType')}
                            rules={[ValidationRules.required]}
                            options={PartnerTypeResourceStore.options}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <RadioGroupField
                            name={formNames.getPath('jahezComissionType')}
                            label={t('Partner.JahezCommissionType')}
                            rules={[ValidationRules.required]}
                            options={JahezComissionTypeResourceStore.options}
                        />
                    </Col>
                    <Col xs={24} md={12} />
                    {jahezComissionType === JahezComissionType.FIXED_AMOUNT && (
                        <Col xs={24} md={12}>
                            <NumberField
                                name={formNames.getPath('jahezComissionAmount')}
                                label={t('Partner.JahezCommissionAmount')}
                                rules={[
                                    ValidationRules.required,
                                    ValidationRules.greaterThanZeroOrZero,
                                    ValidationRules.numberMaxValue(1000000),
                                ]}
                            />
                        </Col>
                    )}
                    {jahezComissionType === JahezComissionType.PERCENTAGE && (
                        <Col xs={24} md={12}>
                            <NumberField
                                name={formNames.getPath('jahezComissionPct')}
                                label={t('Partner.JahezCommissionPct')}
                                rules={[
                                    ValidationRules.required,
                                    ValidationRules.greaterThanZeroOrZero,
                                    ValidationRules.numberMaxValue(100),
                                ]}
                            />
                        </Col>
                    )}
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath('crNumber')}
                            label={t('Partner.CrNumber')}
                            rules={[ValidationRules.crNumber]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath('licenseNumber')}
                            label={t('Partner.LicenseNumber')}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <SelectField
                            name={formNames.getPath('country')}
                            label={t('Partner.Country')}
                            options={CountryResourceStore.options}
                            selectProps={{
                                showSearch: true,
                            }}
                        />
                    </Col>
                    <Col xs={24}>
                        <CheckboxGroupField
                            name={formNames.getPath('specialization')}
                            label={t('Partner.Specialization')}
                            options={SpecializationResourceStore.options}
                        />
                    </Col>
                    <Col xs={24}>
                        <TextAreaField
                            name={formNames.getPath('description')}
                            label={t('Partner.Description')}
                        />
                    </Col>
                </Row>
            </Card>

            <Card title={t('App.Pages.PartnersCreate.Communication')}>
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath([
                                'contact',
                                'supportEmail',
                            ])}
                            label={t('Partner.CompanySupportEmail')}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.email,
                            ]}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <TextField
                            name={formNames.getPath([
                                'contact',
                                'supportPhone',
                            ])}
                            label={t('Partner.CompanySupportPhone')}
                            rules={[ValidationRules.required]}
                        />
                    </Col>
                    <Col xs={24}>
                        <TextAreaField
                            name={formNames.getPath([
                                'contact',
                                'contactPersonInfo',
                            ])}
                            label={t('Partner.ContactPersonInfo')}
                        />
                    </Col>
                </Row>
            </Card>

            <AntForm.List
                name={formNames.getPath('products')!}
                initialValue={[{}]}
            >
                {(fields, { add, remove }) => {
                    return (
                        <Card
                            title={t('App.Pages.PartnersCreate.ProductInfo')}
                            extra={
                                <Button
                                    onClick={() => {
                                        add();
                                    }}
                                    icon={<PlusOutlined />}
                                >
                                    {t('App.Pages.PartnersCreate.AddProduct')}
                                </Button>
                            }
                        >
                            {fields.map((field) => {
                                return (
                                    <Card
                                        key={field.key}
                                        hidden={false}
                                        title=" "
                                        extra={
                                            fields.length > 1 ? (
                                                <Button
                                                    onClick={() => {
                                                        remove(field.name);
                                                    }}
                                                    type="text"
                                                >
                                                    <DeleteOutlined
                                                        style={{
                                                            color: 'red',
                                                        }}
                                                    />
                                                </Button>
                                            ) : undefined
                                        }
                                    >
                                        <Row gutter={24}>
                                            <Col xs={24}>
                                                <TextField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'name',
                                                        ),
                                                    ]}
                                                    label={t('Product.Name')}
                                                    rules={[
                                                        ValidationRules.required,
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24}>
                                                <TextField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'partnerSystemLink',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.PartnerSystemLink',
                                                    )}
                                                    rules={[
                                                        ValidationRules.required,
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24}>
                                                <CheckboxGroupField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'offeringTypes',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.OfferingTypes',
                                                    )}
                                                    rules={[
                                                        ValidationRules.required,
                                                    ]}
                                                    options={
                                                        ProductOfferingTypeResourceStore.options
                                                    }
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'minAmount',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MinAmount',
                                                    )}
                                                    rules={[
                                                        ValidationRules.greaterThanZero,
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'maxAmount',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MaxAmount',
                                                    )}
                                                    rules={[
                                                        ValidationRules.numberMinValue(
                                                            products?.[
                                                                field.name
                                                            ]?.minAmount,
                                                        ),
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                        ValidationRules.numberMaxValue(
                                                            1000000000,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'minTerms',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MinTerms',
                                                    )}
                                                    rules={[
                                                        ValidationRules.greaterThanZero,
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'maxTerms',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MaxTerms',
                                                    )}
                                                    rules={[
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                        ValidationRules.numberMinValue(
                                                            products?.[
                                                                field.name
                                                            ]?.minTerms,
                                                        ),
                                                        ValidationRules.numberMaxValue(
                                                            120,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'minMurabahaRatePct',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MinMurabahaRatePct',
                                                    )}
                                                    rules={[
                                                        ValidationRules.greaterThanZero,
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <NumberField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'maxMurabahaRatePct',
                                                        ),
                                                    ]}
                                                    label={t(
                                                        'Product.MaxMurabahaRatePct',
                                                    )}
                                                    rules={[
                                                        ValidationRules.digits(
                                                            12,
                                                            2,
                                                        ),
                                                        ValidationRules.numberMinValue(
                                                            products?.[
                                                                field.name
                                                            ]
                                                                ?.minMurabahaRatePct,
                                                        ),
                                                    ]}
                                                />
                                            </Col>
                                            <Col xs={24} md={24}>
                                                <TextAreaField
                                                    name={[
                                                        field.name,
                                                        productFormNames.getPath(
                                                            'description',
                                                        ) as string,
                                                    ]}
                                                    label={t(
                                                        'Product.Description',
                                                    )}
                                                />
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            })}
                        </Card>
                    );
                }}
            </AntForm.List>
        </>
    );
});

export type CreatePartnerFormValues = CreatePartnerRequest;

const formNames = FormHelper.createNameProvider<CreatePartnerFormValues>();
const productFormNames = FormHelper.createNameProvider<CreateProductRequest>();
